import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { authService, LoginCredentials, SignupData } from '../services/auth';

interface AuthContextType {
  token: string | null;
  userEmail: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(authService.getToken());

  useEffect(() => {
    const syncToken = () => {
      setToken(authService.getToken());
    };

    window.addEventListener('storage', syncToken);
    window.addEventListener('ecopredict-auth-token-changed', syncToken as EventListener);

    return () => {
      window.removeEventListener('storage', syncToken);
      window.removeEventListener('ecopredict-auth-token-changed', syncToken as EventListener);
    };
  }, []);

  const userEmail = useMemo(() => {
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }

      const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized));
      return decoded?.email ?? null;
    } catch {
      return null;
    }
  }, [token]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);
    authService.storeToken(response.token);
    setToken(response.token);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const response = await authService.loginWithGoogle();
    authService.storeToken(response.token);
    setToken(response.token);
  }, []);

  const signup = useCallback(async (data: SignupData) => {
    await authService.signup(data);
  }, []);

  const logout = useCallback(() => {
    authService.removeToken();
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({ token, userEmail, isAuthenticated: Boolean(token), login, loginWithGoogle, signup, logout }),
    [token, userEmail, login, loginWithGoogle, signup, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
